import { html } from "lit";
import { MenuItem } from "@omegagrid/core";
import { NodeModel, TreeSource } from "@omegagrid/tree";
import { msg } from "@omegagrid/localize";
import rp, { BrowserAdapter, BrowserOptions, formatDateTime } from "@reporting/rp-app-host";
import { StatementDefinition } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";

export class FinanceBrowserAdapter extends BrowserAdapter {

	getTooltip(node: NodeModel) {
		if (!node.properties?.statement) return null;
		const statement = node.properties.statement as StatementDefinition;
		return html`
			<div>
				ID: ${statement.id_statement}<br>
				${statement.description ? html`${msg('Description')}: ${statement.description}<br>` : ''}
				${statement.remark ? html`${msg('Remark')}: ${statement.remark}<br>` : ''}
				${msg('Created')}: ${formatDateTime(statement.date_created)}
				${node.properties.readOnly ? html`<br>${msg('Shared from')}: #${statement.accounting}` : ''}
			</div>
		`;
	}

	async getTree(): Promise<TreeSource> {
		return [{
			k: `definition-finReports`,
			v: msg('Reports'),
			l: 0,
			icon: { icon: ['fas', 'files'], color: 'DarkGreen' },
			props: { menuItems: [
				{ key: 'newReport', value: msg('New report'), icon: 'square-plus' },
				{ key: 'reportsStatistics', value: msg('Statistics'), icon: 'chart-bar' },
				...(rp.userProvider.isSA() ? [{ key: 'reportRecovery', value: msg('Report recovery'), icon: 'clock-rotate-left' }] : []),
			]}
		}, {
			k: `definition-dimensions-${MODULE_CODES.FINANCE}`,
			v: msg('Dimensions'),
			l: 0,
			icon: { icon: ['fas', 'cube'], color: '#2C63A1' }
		}, {
			k: 'definition-finPeriodClosing',
			v: msg('Accounting period closing'),
			l: 0,
			icon: { icon: 'calendar-xmark', color: 'Brown' },
		}, {
			k: 'definition-finAccounts',
			v: msg('Accounts'),
			l: 0,
			icon: { icon: 'square-dollar', color: 'GoldenRod' }
		}, {
			k: 'definition-attributes-accounts',
			v: `${msg('Accounts')} - ${msg('Attributes')}`,
			l: 0,
			icon: { icon: 'brackets-curly', color: 'GoldenRod' }
		}, {
			k: 'definition-attributes-finDetail',
			v: `${msg('Detail')} - ${msg('Attributes')}`,
			l: 0,
			icon: { icon: 'brackets-curly', color: 'GoldenRod' }
		}, {
			k: `definition-drillColumns-${MODULE_CODES.FINANCE}`,
			v: `${msg('Detail')} - ${msg('Columns')}`,
			l: 0,
			icon: { icon: 'table-list', color: 'DarkGreen' }
		}, {
			k: `definition-detailCustom-${MODULE_CODES.FINANCE}`,
			v: `${msg('Detail')} - ${msg('Custom')}`,
			l: 0,
			icon: { icon: 'shapes', color: 'Chocolate' }
		}, {
			k: 'definition-scenarios',
			v: msg('Scenarios'),
			l: 0,
			icon: { icon: 'scroll', color: 'LightSlateGray' }
		}, {
			k: 'definition-fiscalYears',
			v: msg('Financial years'),
			l: 0,
			icon: { icon: 'calendar', color: 'DodgerBlue' }
		}, {
			k: `definition-securityRoles-${MODULE_CODES.FINANCE}`,
			v: msg('Security roles'),
			l: 0,
			icon: { icon: 'lock-keyhole', color: 'DarkSlateBlue' }
		}];
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const items: MenuItem[] = [];
		if (node.properties?.menuItems) {
			items.push(...node.properties.menuItems as MenuItem[]);
		}
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const statement = node.properties?.statement as StatementDefinition;
		switch (key) {
			case 'newReport':
				rp.host.openModalSidebar({ id: 'definition-finReportForm', caption: msg('New report') });
				break;
			case 'reportsStatistics':
				rp.host.open('definition-reportStats', { title: msg('Statistics') });
				break;
			case 'reportRecovery':
				rp.host.open('definition-reportRecovery', { title: msg('Report Recovery') });
				break;
		}
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			contextMenuButton: true,
		});
	}
}
